/**
 * PerimetroIncendioLayer.tsx
 *
 * Layer Leaflet che disegna:
 *   - Marker stella rossa pulsante = punto d'innesco
 *   - Polilinea / Poligono = perimetro incendio
 *   - Vertici draggabili + midpoint per inserimento
 *   - Segmenti colorati fronte/fianchi/retro
 *
 * © 2024 IRTILAC — Angelo Marino — Tutti i diritti riservati.
 */

import React from 'react';
import { Marker, Polygon, Polyline, Popup } from 'react-leaflet';
import L from 'leaflet';
import { AIB_COLORS } from '@/config/sitac';
import { decimalToDMS } from '../utils/perimetroUtils';

interface PerimetroIncendioLayerProps {
  puntiPerimetro: [number, number][];
  perimetroMode: boolean;
  perimetroChiuso: boolean;
  pendingVertexIndex?: number | null;
  puntoInnesco?: [number, number] | null;

  onMoveVertex?: (index: number, lat: number, lng: number) => void;
  onInsertVertex?: (index: number, lat: number, lng: number) => void;
  onDeleteVertex?: (index: number) => void;
  onMoveInnesco?: (lat: number, lng: number) => void;

  activeFrontSegment?: [[number, number], [number, number]] | null;
  shelteredRearSegment?: [[number, number], [number, number]] | null;
  rightFlankSegment?: [[number, number], [number, number]] | null;
  leftFlankSegment?: [[number, number], [number, number]] | null;
}

// ─── Icona stella rossa per il punto d'innesco ────────────────
function buildInnescoIcon(): L.DivIcon {
  // Stella a 5 punte SVG inline, rossa, con glow pulsante
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
      <defs>
        <filter id="glow">
          <feGaussianBlur stdDeviation="2.5" result="coloredBlur"/>
          <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      <polygon
        points="18,3 21.5,13.5 32,13.5 23.5,20 26.5,31 18,24.5 9.5,31 12.5,20 4,13.5 14.5,13.5"
        fill="${AIB_COLORS.innescoPoint}"
        stroke="#fff"
        stroke-width="1.8"
        filter="url(#glow)"
      />
    </svg>`;

  return L.divIcon({
    html: `
      <div style="
        position:relative;
        width:36px;
        height:36px;
        animation: innesco-pulse 1.6s ease-in-out 5 forwards;
      ">
        ${svg}
      </div>
      <style>
        @keyframes innesco-pulse {
          0%,100% { transform: scale(1);   filter: drop-shadow(0 0 4px ${AIB_COLORS.innescoPoint}); }
          50%      { transform: scale(1.18); filter: drop-shadow(0 0 10px ${AIB_COLORS.fronteAttivo}); }
        }
      </style>`,
    className: '',
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -20],
  });
}

const INNESCO_ICON = buildInnescoIcon();

// ─────────────────────────────────────────────────────────────

export const PerimetroIncendioLayer: React.FC<PerimetroIncendioLayerProps> = ({
  puntiPerimetro,
  perimetroMode,
  perimetroChiuso,
  pendingVertexIndex = null,
  puntoInnesco = null,
  onMoveVertex,
  onInsertVertex,
  onDeleteVertex,
  onMoveInnesco,
  activeFrontSegment,
  shelteredRearSegment,
  rightFlankSegment,
  leftFlankSegment,
}) => {

  // Midpoint markers per inserimento vertici
  const midpoints = puntiPerimetro
    .map((p, i) => {
      const isLast = i === puntiPerimetro.length - 1;
      const next = isLast
        ? perimetroChiuso ? puntiPerimetro[0] : null
        : puntiPerimetro[i + 1];
      if (!next) return null;
      return {
        key: `mid-${i}`,
        insertIndex: i + 1,
        position: [(p[0] + next[0]) / 2, (p[1] + next[1]) / 2] as [number, number],
      };
    })
    .filter(Boolean) as { key: string; insertIndex: number; position: [number, number] }[];

  return (
    <>
      {/* ── Punto d'innesco — stella rossa ── */}
      {puntoInnesco && (
        <Marker
          position={puntoInnesco}
          icon={INNESCO_ICON}
          draggable={!perimetroChiuso}
          eventHandlers={{
            dragend: (e) => {
              const pos = e.target.getLatLng();
              onMoveInnesco?.(pos.lat, pos.lng);
            },
          }}
          zIndexOffset={2000}
        >
          <Popup>
            <div style={{ minWidth: 190, fontFamily: 'sans-serif', fontSize: 13 }}>
              <div style={{ fontWeight: 700, color: AIB_COLORS.innescoPoint, marginBottom: 6, fontSize: 14 }}>
                Punto d'Innesco
              </div>
              <div style={{ lineHeight: 1.7, color: '#333' }}>
                <span style={{ display: 'block' }}>{decimalToDMS(puntoInnesco[0], true)}</span>
                <span style={{ display: 'block' }}>{decimalToDMS(puntoInnesco[1], false)}</span>
                <span style={{ display: 'block', fontSize: 11, color: '#888', marginTop: 4 }}>
                  {puntoInnesco[0].toFixed(6)}, {puntoInnesco[1].toFixed(6)}
                </span>
              </div>
              {!perimetroChiuso && (
                <div style={{ marginTop: 6, fontSize: 11, color: '#666' }}>
                  Trascina per spostare
                </div>
              )}
            </div>
          </Popup>
        </Marker>
      )}

      {/* ── Linea di collegamento innesco → primo vertice ── */}
      {puntoInnesco && puntiPerimetro.length > 0 && !perimetroChiuso && (
        <Polyline
          positions={[puntoInnesco, puntiPerimetro[0]]}
          pathOptions={{
            color: AIB_COLORS.innescoPoint,
            weight: 2,
            dashArray: '6,5',
            opacity: 0.55,
          }}
        />
      )}

      {/* ── Perimetro: polilinea (aperto) ── */}
      {puntiPerimetro.length >= 2 && (
        <Polyline
          positions={puntiPerimetro}
          pathOptions={{
            color: AIB_COLORS.perimetroBase,
            weight: 3,
            dashArray: perimetroChiuso ? undefined : '8,6',
          }}
        />
      )}

      {/* ── Perimetro: poligono fill (chiuso) ── */}
      {perimetroChiuso && puntiPerimetro.length >= 3 && (
        <Polygon
          positions={puntiPerimetro}
          pathOptions={{
            color: AIB_COLORS.perimetroBase,
            weight: 3,
            fillColor: AIB_COLORS.perimetroBase,
            fillOpacity: 0.14,
          }}
        />
      )}

      {/* ── Segmenti colorati analisi vento ── */}
      {activeFrontSegment && (
        <Polyline
          positions={activeFrontSegment}
          pathOptions={{ color: AIB_COLORS.fronteAttivo, weight: 7, opacity: 0.98 }}
        />
      )}
      {shelteredRearSegment && (
        <Polyline
          positions={shelteredRearSegment}
          pathOptions={{ color: AIB_COLORS.retroProtetto, weight: 6, opacity: 0.92, dashArray: '10,8' }}
        />
      )}
      {rightFlankSegment && (
        <Polyline
          positions={rightFlankSegment}
          pathOptions={{ color: AIB_COLORS.fiancoDestro, weight: 6, opacity: 0.94 }}
        />
      )}
      {leftFlankSegment && (
        <Polyline
          positions={leftFlankSegment}
          pathOptions={{ color: AIB_COLORS.fiancoSinistro, weight: 6, opacity: 0.94 }}
        />
      )}

      {/* ── Vertici draggabili ── */}
      {perimetroMode &&
        puntiPerimetro.map((p, i) => {
          const isPending = pendingVertexIndex === i;
          return (
            <Marker
              key={`perim-${i}`}
              position={p}
              draggable
              eventHandlers={{
                dragend: (e) => {
                  const pos = e.target.getLatLng();
                  onMoveVertex?.(i, pos.lat, pos.lng);
                },
                dblclick: () => onDeleteVertex?.(i),
                contextmenu: () => onDeleteVertex?.(i),
              }}
              icon={L.divIcon({
                html: `<div style="
                  width:18px; height:18px;
                  background:${isPending ? '#facc15' : AIB_COLORS.perimetroBase};
                  border:2px solid #fff; border-radius:50%;
                  box-shadow:0 0 6px ${isPending ? '#facc15' : AIB_COLORS.perimetroBase};
                "></div>`,
                className: '',
                iconSize: [18, 18],
                iconAnchor: [9, 9],
              })}
            />
          );
        })}

      {/* ── Midpoint markers per inserimento vertici ── */}
      {perimetroMode &&
        midpoints.map((m) => (
          <Marker
            key={m.key}
            position={m.position}
            eventHandlers={{
              click: () => onInsertVertex?.(m.insertIndex, m.position[0], m.position[1]),
            }}
            icon={L.divIcon({
              html: `<div style="width:14px;height:14px;background:${AIB_COLORS.perimetroBase}66;border:2px dashed #fff;border-radius:50%;box-shadow:0 0 4px ${AIB_COLORS.perimetroBase}99"></div>`,
              className: '',
              iconSize: [14, 14],
              iconAnchor: [7, 7],
            })}
          />
        ))}
    </>
  );
};

export default PerimetroIncendioLayer;
